import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import ChannelsFilters from '@r1-frontend/ui-react/components/Channels/ChannelsFilters';
import ChannelsList from '@r1-frontend/ui-react/components/Channels/ChannelsList';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import { TState } from '~/src/store';
import {
    clearChannelsBundleId,
    clearChannelThemeSelection,
    clearSearchChannels,
    getAllChannels,
    getChannelPackages,
    getChannelThemes,
    getOfferChannelPackages,
    onChangeChannelThemeAction,
    searchChannels,
    setFilterForChannelsAction,
} from '~/src/store/actions/bundles';
import { getTariffChannels, getTariffChannelsWithId } from '~/src/store/selectors/bundles';
import { getChannelThemesWithCounts } from '~/src/store/selectors/bundles/getChannelThemesWithCounts';

import * as ST from './styled';

interface IChannelListPopup {
    freePackets?: number | string | number[],
    onClose: () => void,
    isOpen: boolean,
    isOfferChannels?: boolean,
}

const ChannelListPopup = ({ freePackets, isOpen, onClose, isOfferChannels }: IChannelListPopup): JSX.Element => {
    const dispatch = useDispatch();
    const {
        city,
        allChannels,
        channelThemes,
        providerId,
        channels,
        tariffChannels,
        preparedThemes,
        selectedFilter,
        channelsFiltersIsApply,
    } = useSelector((state: TState) => ({
        city: state.city.currentCity,
        providerId: state.city.provider?.providerId,
        allChannels: state.bundles.allChannels,
        channelThemes: state.bundles.channelThemes,
        channels: getTariffChannelsWithId(state),
        tariffChannels: getTariffChannels(state),
        preparedThemes: getChannelThemesWithCounts(state),
        channelsFiltersIsApply: state.bundles.channelsFiltersIsApply,
        selectedFilter: state.bundles.selectedFilter,
    }));

    const [isShowSearch, setIsShowSearch] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [value, setValue] = useState('');

    const isMobile = useMatchMedia(`(max-width: ${wideBreakpoints.mobile})`);
    const searchRef = useRef<HTMLInputElement>(null);

    const toogleShowSearchInput = (flag: boolean) => {
        setIsShowSearch(flag);
    };

    const onClickPopupWrapper = useCallback(() => {
        setIsShowSearch(false);
        dispatch(clearSearchChannels());
    }, [dispatch]);

    const onCloseHandler = useCallback(() => {
        // Сбрасываем фильтры и очищаем поиск
        dispatch(clearChannelThemeSelection());
        dispatch(clearSearchChannels());
        dispatch(clearChannelsBundleId());
        dispatch(setFilterForChannelsAction({
            value: 0,
            label: 'Все каналы',
        }));
        onClose();
    }, [dispatch, onClose]);

    const onApplySearch = useCallback((search = '') => {
        dispatch(searchChannels(search.toLowerCase().trim()));
    }, [dispatch]);

    const onClearSearch = useCallback(() => {
        setValue('');
        dispatch(clearSearchChannels());
    }, [dispatch]);

    const onChangeSearch = useCallback((e) => {
        setValue(e.target.value);
        onApplySearch(e.target.value);
    }, [onApplySearch]);

    const onShowSearchInput = useCallback((e) => {
        e.stopPropagation();
        toogleShowSearchInput(true);
    }, [toogleShowSearchInput]);

    const onChangeChannelTheme = useCallback((data) => {
        dispatch(onChangeChannelThemeAction(data, channelThemes));
    }, [dispatch, channelThemes]);

    useEffect(() => {
        if (isShowSearch) {
            searchRef.current?.focus();
        }
    }, [isShowSearch]);

    useEffect(() => {
        if (!channelThemes?.length && isOpen) {
            dispatch(getChannelThemes());
        }
    }, [dispatch, channelThemes?.length, isOpen]);

    useEffect(() => {
        if (isOpen && !allChannels?.length) {
            dispatch(getAllChannels(providerId));
        }
        if (isOpen && allChannels?.length) {
            setIsLoading(false);
        }
        if (freePackets) {
            isOfferChannels
                ? dispatch(getOfferChannelPackages(freePackets))
                : dispatch(getChannelPackages(freePackets));
        }
    }, [dispatch, isOpen, allChannels?.length, freePackets, city, providerId]);

    return (
        <ST.SBasePopup
            isOpen={isOpen}
            width='600px'
            height='645px'
            title={isMobile && isShowSearch
                ? <ST.HideSearchBtn
                    text='Назад'
                    type={btnTypes.GHOST}
                    size={btnSizes.MIDDLE}
                    iconsSprite={'/static/images/icons/arrow_left_sprite.svg'}
                    symbolId='back-arrow-2'
                    data-test='channel-list-popup-back-btn'
                />
                : 'Список телеканалов'
            }
            description={
                <ChannelsFilters
                    isShowSearch={isShowSearch}
                    channels={channels}
                    selectedFilter={selectedFilter}
                    preparedThemes={preparedThemes}
                    onChangeChannelTheme={onChangeChannelTheme}
                    onShowSearchInput={onShowSearchInput}
                    onClearSearch={onClearSearch}
                    onChangeSearch={onChangeSearch}
                    value={value}
                    data-test='channel-list-popup-filters'
                />
            }
            onClose={onCloseHandler}
            onClick={onClickPopupWrapper}
            data-test='channel-list-popup'
        >
            {isLoading
                ? <ST.SThrobber data-test='channel-list-popup-loader'/>
                : <ChannelsList
                    channelsFiltersIsApply={channelsFiltersIsApply}
                    channels={tariffChannels}
                    data-test='channel-list-popup-channels'
                />
            }
        </ST.SBasePopup>
    );
};

export default ChannelListPopup;
